import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment";

import "react-dates/initialize";

import { generateQueryString } from "../../utils/queryString";
import handleFilterChange from "../../utils/handleFilterChange";
import { ITourismEvents } from "../../lib/models/ITourismEvents";

import Hexagon from "../svg/Hexagon";
import getTourismEvents from "../../lib/api/tourismEventsApi";

import TourismEventResult from "./TourismEventResult";
import SkeletonCard from "./SkeletonCard";
import Pagination from "../shared/Pagination";
import Filters from "../shared/Filters/Filters";

moment.locale("en-GB");
const dateFormat = "YYYYMMDD";

export default function TourismEvents({ currentPageRef, pageSize }) {
  const urlParams = new URLSearchParams(window.location.search);

  const dateFrom = urlParams.get("dateFrom");
  const dateTo = urlParams.get("dateTo");

  const initialFilters = {
    page: parseInt(urlParams.get("page")) || 1,
    eventType: urlParams.getAll("eventType"),
    dateFrom: dateFrom ? moment(dateFrom, dateFormat).format(dateFormat) : null,
    dateTo: dateTo ? moment(dateTo, dateFormat).format(dateFormat) : null,
    search: urlParams.get("search") || ""
  };

  const [results, updateResults]: [ITourismEvents, React.Dispatch<any>] = useState(null);

  const [filters, updateFilters] = useState(initialFilters);
  const [loading, updateLoading] = useState(true);

  const loadTourismEvents = () => {
    updateLoading(true);
    getTourismEvents(
      currentPageRef,
      filters.page,
      filters.eventType,
      filters.search,
      filters.dateFrom,
      filters.dateTo
    )
      .then((e) => {
        const clientQueryString = generateQueryString(filters);
        window.history.replaceState(null, null, `?${clientQueryString}`);
        updateResults(e);
        updateLoading(false);
      })
      .catch((e) => {
        console.error(e);
        return toast.error("Something went wrong. Please try again later");
      });
  };

  useEffect(loadTourismEvents, [filters]);

  const handleFiltering = (filterValue) => {
    handleFilterChange("replace", filterValue, filters, updateFilters);
  };

  const renderResults = () => {
    if (results !== null && !loading) {
      return results.Results.map((e, i) => (
        <TourismEventResult
          key={e.Id}
          number={i}
          data={e}
          page={filters.page}
        />
      ));
    }
    return [...Array(pageSize)].map((e, i) => (
      <SkeletonCard key={i} number={i} page={filters.page} />
    ));
  };

  const renderFilters = () => {
    const typeOptions = results.EventTypeOptions.map((option) => ({
      name: option.Text,
      value: option.Value,
      isSelected: filters.eventType.includes(option.Value)
    }));

    return (
      <Filters
        applyFilter={handleFiltering}
        sections={[
          {
            name: "eventType",
            className: "event",
            options: typeOptions
          },
          {
            className: "date",
            isDateSection: true
          }
        ]}
        searchProps={{
          searchValue: filters.search,
          label: "Events",
          doSearch: loadTourismEvents
        }}
        dateFilter={{
          startDate: filters.dateFrom
            ? moment(filters.dateFrom, dateFormat)
            : null,
          endDate: filters.dateTo ? moment(filters.dateTo, dateFormat) : null
        }}
      />
    );
  };

  return (
    <>
      <ToastContainer />
      <div className="container main-content">
        {results && renderFilters()}
        <div className="row results">
          {renderResults()}
          {results?.Results.length === 0 && (
            <div className="col-12">
              <div
                className="no-results"
                dangerouslySetInnerHTML={{
                  __html: results.NoResultsContent
                }}
              ></div>
            </div>
          )}
        </div>

        <nav
          className="row justify-content-center pagination"
          role="navigation"
          aria-label="Pagination navigation"
        >
          {results && (
            <Pagination
              pageSize={pageSize}
              totalItems={results.TotalItems}
              currentPage={filters.page}
              updatePage={handleFilterChange}
              filters={filters}
              updateFilters={updateFilters}
              featuredItems={1}
            />
          )}
        </nav>
        {results?.Results.length > 5 && (
          <>
            <Hexagon id={2} />
            <Hexagon id={3} />
          </>
        )}
      </div>
    </>
  );
}
